import { InputHTMLAttributes } from 'react'
import {
  UseFormRegister,
  FieldValues,
  RegisterOptions,
  Path,
  GlobalError,
  FieldErrorsImpl,
} from 'react-hook-form'
import { NAMESPACE } from '../utils'
import { get } from 'lodash'
interface RadioButtonOption {
  value: string
  label: string
  checked?: boolean
  disabled?: boolean
}

interface FieldErrors<T extends FieldValues> {
  root?: Record<string, GlobalError> & GlobalError
  [key: string]: FieldErrorsImpl<T> | GlobalError | undefined
}
interface RadioGroupProps<T extends FieldValues>
  extends InputHTMLAttributes<HTMLInputElement> {
  options: RadioButtonOption[]
  name: Path<T>
  register?: UseFormRegister<T>
  validation?: RegisterOptions<T, Path<T>>
  errors?: FieldErrors<T>
  labelClasses?: string
  dataQaId?: string
  flexDirection?: string
  optionClasses?: string
  radioGroupId?: string
  customStyles?: object
  layoutStyle?: string
  parentClasses?: string
}

const CustomRadioGroup = <T extends FieldValues>({
  options,
  name,
  register,
  errors,
  validation = {},
  dataQaId = '',
  defaultValue,
  labelClasses = 'break-all',
  optionClasses = '',
  radioGroupId = '',
  customStyles = {},
  parentClasses = '',
  ...rest
}: RadioGroupProps<T>) => {
  const error = get(errors, name)
  return (
    <div
      className={`${
        rest?.layoutStyle ? rest?.layoutStyle : `flex ${rest?.flexDirection}`
      } font-primary font-normal`}
      id={radioGroupId}
    >
      {options.map((option) => (
        <div key={option.value} className={optionClasses}>
          <label
            className={`inline-flex items-center py-2 cursor-pointer ${
              option.disabled ? 'cursor-not-allowed opacity-50' : ''
            } ${parentClasses}`}
            style={{ ...customStyles }}
          >
            <input
              type="radio"
              value={option?.value}
              style={{ ...customStyles }}
              checked={option?.checked}
              defaultChecked={defaultValue === option?.value}
              data-qa-id={dataQaId || ''}
              {...(register ? register(name, { ...validation }) : { name })}
              disabled={option?.disabled}
              className={`accent-secondary-500`}
              {...rest}
            />
            <span
              className={`ml-2 mr-4 text-accent-900 ${labelClasses}  ${
                option?.disabled ? 'text-accent-400' : ''
              }`}
              style={{ ...customStyles }}
            >
              {option.label}
            </span>
          </label>
        </div>
      ))}
      {error && (
        <div className="max-w-[calc(100% - 1.5rem)]">
          {error?.type === 'required' && !error?.message && (
            <span
              className="text-error-500 text-xxs font-primary font-normal"
              style={{ ...customStyles }}
            >
              {NAMESPACE?.REQUIRED_FIELD}
            </span>
          )}
          {error?.message && (
            <span
              className="text-error-500 text-xxs font-primary font-normal w-full text-wrap inline-block leading-[1.3]"
              style={{ ...customStyles }}
            >
              {typeof error.message === 'string'
                ? error.message
                : JSON.stringify(error.message)}
            </span>
          )}
        </div>
      )}
    </div>
  )
}
export default CustomRadioGroup
